// client/src/components/messaging/SeenByIndicator.tsx
// Displays "Seen by <name> • <time>" for participants who have viewed the thread

import { useMemo, useState } from 'react';
import type { ThreadParticipant } from '../../types/api';

interface SeenByIndicatorProps {
  participants: ThreadParticipant[];
  lastMessageAt: string | null;
  currentUserId?: string;
}

/**
 * Formats a relative time string (e.g., "2m ago", "1h ago", "Yesterday")
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * Displays read receipt information for thread participants.
 * Shows who has seen the latest messages and when.
 */
export default function SeenByIndicator({
  participants,
  lastMessageAt,
  currentUserId,
}: SeenByIndicatorProps) {
  const [showAll, setShowAll] = useState(false);

  // Filter participants who have seen messages after the last message
  // Exclude current user from the "seen by" list
  const seenParticipants = useMemo(() => {
    if (!lastMessageAt) return [];

    const lastMsgTime = new Date(lastMessageAt).getTime();

    return participants
      .filter((p) => {
        // Skip current user
        if (currentUserId && p.userId === currentUserId) return false;
        // Must have a lastSeenAt timestamp
        if (!p.lastSeenAt) return false;
        // lastSeenAt must be >= lastMessageAt (they've seen the latest message)
        return new Date(p.lastSeenAt).getTime() >= lastMsgTime;
      })
      .sort((a, b) => {
        // Sort by most recently seen first
        const aTime = new Date(a.lastSeenAt!).getTime();
        const bTime = new Date(b.lastSeenAt!).getTime();
        return bTime - aTime;
      });
  }, [participants, lastMessageAt, currentUserId]);

  // Nothing to show if no one has seen the latest message
  if (seenParticipants.length === 0) {
    return null;
  }

  const mostRecent = seenParticipants[0];
  const othersCount = seenParticipants.length - 1;

  return (
    <div className="relative inline-flex items-center">
      <button
        onClick={() => setShowAll(!showAll)}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        title={`Seen by ${seenParticipants.length} ${seenParticipants.length === 1 ? 'person' : 'people'}`}
      >
        {/* Double check icon for read receipt */}
        <svg
          className="w-3.5 h-3.5 text-blue-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m0 0l4-4m-4 4l-4-4m4 4l4 4"
          />
        </svg>
        <span>
          Seen by {mostRecent.user?.fullName || 'Unknown'}
          {othersCount > 0 && ` +${othersCount}`}
        </span>
        <span className="text-gray-400">•</span>
        <span className="text-gray-400">{formatRelativeTime(mostRecent.lastSeenAt!)}</span>
      </button>

      {/* Expanded list of all who have seen */}
      {showAll && seenParticipants.length > 1 && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-2">
          <div className="px-3 py-1.5 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-500">Seen by</span>
          </div>
          <ul className="max-h-40 overflow-y-auto">
            {seenParticipants.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between px-3 py-1.5 hover:bg-gray-50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[10px] font-medium text-gray-600 flex-shrink-0">
                    {p.user?.fullName?.charAt(0) || '?'}
                  </div>
                  <span className="text-xs text-gray-700 truncate">
                    {p.user?.fullName || 'Unknown'}
                  </span>
                </div>
                <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                  {formatRelativeTime(p.lastSeenAt!)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
